// M5.5: admin page for the per-team status_transition_xp matrix.
//
// Reachable from the admin nav (kept for backward compat with
// bookmarks and old links). The primary entry point is now the
// "Transitions" button on each Jira ingestor's detail page —
// see IngestorDetailPage. This page exists for:
//   1. Bookmarks/old links that point at /admin/status-transitions
//   2. Teams that have a Jira ingestor but the user wants to
//      reach the matrix without going through the ingestor
//
// When the page detects a Jira ingestor for the selected team,
// it shows the same matrix as the ingestor-scoped page. If no
// Jira ingestor exists for the picked team, the matrix is shown
// with a free-text hint (the matrix component handles this).

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { TransitionsMatrix } from './TransitionsMatrix';

interface Team {
  id: string;
  name: string;
  slug: string;
}
interface IngestorInstance {
  id: string;
  type: string;
  team_id: string;
  name: string;
  enabled: boolean;
}

export function StatusTransitionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const teamIdFromUrl = searchParams.get('team_id') ?? '';

  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });

  // URL-driven team picker (same pattern as PublicBoardSettingsPage).
  // Default to the first team if the URL doesn't have one and teams
  // have loaded.
  const [filterTeam, setFilterTeam] = useState<string>(teamIdFromUrl);
  useEffect(() => {
    if (teamIdFromUrl) {
      setFilterTeam(teamIdFromUrl);
      return;
    }
    // v0.6.2: the surrounding `if` chain is safe, but the
    // body that reads `teams.data.teams[0].id` is not.
    // Reach for the same value via the alias used in the
    // guard so the chain is consistent — if the guard
    // passes, the body sees the same field without an
    // unsafe direct access.
    const firstTeam = teams.data?.teams?.[0];
    if (!filterTeam && firstTeam?.id) {
      const firstTeamId = firstTeam.id;
      setFilterTeam(firstTeamId);
      setSearchParams((p) => {
        const next = new URLSearchParams(p);
        next.set('team_id', firstTeamId);
        return next;
      }, { replace: true });
    }
  }, [teamIdFromUrl, teams.data, filterTeam, setSearchParams]);

  // Find the team's Jira ingestor so we can offer Jira status
  // autocompletion in the matrix. The matrix accepts jiraInstanceId
  // as an optional prop — if absent, the from/to fields are
  // free-text with a hint.
  const instances = useQuery({
    queryKey: ['ingestor-instances', filterTeam],
    queryFn: () => api<{ instances: IngestorInstance[] }>(`/api/admin/ingestor-instances?team_id=${filterTeam}`),
    enabled: !!filterTeam,
  });
  const jiraInstance = instances.data?.instances?.find((i) => i.type === 'jira' && i.enabled);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Status transition XP</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Award XP when a teammate moves a Jira issue from one status to another.
            Leave <em>From</em> empty to match any source status.
          </p>
          <p className="mt-1 text-sm text-ink-muted">
            Attribution: non-terminal moves credit whoever made them; moves into a
            terminal status (<em>Done</em>/<em>Cancelled</em>, per the team's terminal
            statuses) credit the ticket's assignee instead — the owner gets the win.
            If the ticket has no assignee, or theirs isn't mapped, the mover is credited.
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-ink-muted" htmlFor="filter-team">Team</label>
          <select
            id="filter-team"
            className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            value={filterTeam}
            onChange={(e) => {
              setFilterTeam(e.target.value);
              setSearchParams((p) => {
                const next = new URLSearchParams(p);
                next.set('team_id', e.target.value);
                return next;
              });
            }}
          >
            {teams.data?.teams?.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </header>

      {filterTeam ? (
        <TransitionsMatrix
          teamId={filterTeam}
          jiraInstanceId={jiraInstance?.id}
        />
      ) : (
        <p className="text-ink-muted">Select a team to view its transitions.</p>
      )}
    </div>
  );
}
